import Script from 'next/script';
import { useEffect } from 'react';

import { appendUtmToUrl } from 'utils/attribution/utm';
import { forceHttps } from 'utils/strings/slugs';

import type { CSSProperties, FC } from 'react';

type WistiaFormEmbedProps = {
  formId?: string;
  embedType?: 'live-event' | 'form';
  /**
   * Optional URL to send the visitor to after a successful submission. Use this
   * when the redirect can't be configured in Wistia itself (e.g. live-event
   * forms). Left empty, the form keeps Wistia's default post-submit behaviour.
   */
  redirectUrl?: string;
  /**
   * Field layout inside the hosted form. `grid` (default) keeps Wistia's
   * two-per-row layout; `inline` forces every field full-width for a
   * single-column form. Applied via a host class the `::part(field)` rules in
   * global.css react to (Wistia lays fields out inside its shadow DOM).
   */
  layout?: 'grid' | 'inline';
};

// Wistia renders the form inside a shadow DOM whose theme is driven by CSS
// custom properties declared on :host (dark by default). Custom properties
// inherit through the shadow boundary, so setting them on the host element
// overrides those defaults. The values mirror the site's Marketo light form
// (molecules/Form/styles) so Wistia forms look identical: white card, dark
// labels, #EDEDF5 inputs, and the teal gradient submit button. Structural
// bits Wistia can't express as variables (full-width button, the asterisk
// requiredness indicator) are handled with ::part() rules in global.css.
const lightTheme = {
  '--form-background-color': '#ffffff',
  '--form-text-color': '#101828',
  '--form-heading-color': '#101828',
  '--input-background-color': '#ededf5',
  '--input-text-color': '#101828',
  '--input-border-color': '#e4e6f1',
  '--input-requiredness-text-color': '#101828',
  '--button-text-color': '#101828',
  '--button-hover-text-color': '#101828',
  '--button-background': 'linear-gradient(180deg, #8ce1e4 0%, #65d5da 100%)',
  '--button-border-color': 'transparent',
  '--input-border-radius': '8px',
  '--button-border-radius': '8px',
  '--form-border-radius': '16px',
  '--date-time-background-color': 'transparent',
} as unknown as CSSProperties;

// Wistia's <wistia-form> web component dispatches its lifecycle events from
// inside a shadow DOM. Composed CustomEvents cross that boundary and bubble to
// `document`, so we listen there. Wistia has renamed this event across embed
// versions, so we cover the known spellings and redirect on the first one.
const WISTIA_SUBMIT_EVENTS = ['wistia-form:submit', 'wistia-form:conversion', 'wistiaform:submit', 'form:submit'];

/**
 * Renders the Wistia form/live-event web component plus its loader script.
 * Shared by WistiaForm and WistiaSplit. next/script dedupes by `id`, so the
 * loader is fetched once even when several embeds render on the same page.
 */
const WistiaFormEmbed: FC<WistiaFormEmbedProps> = ({
  formId,
  embedType = 'live-event',
  redirectUrl,
  layout = 'grid',
}) => {
  useEffect(() => {
    const base = redirectUrl && (forceHttps(redirectUrl) || redirectUrl);
    if (!base || !formId) {
      return undefined;
    }

    let redirected = false;
    const redirect = () => {
      if (redirected) {
        return;
      }
      redirected = true;
      // Carry the visitor's UTM params onto the thank-you URL so attribution
      // survives the redirect. Read at redirect time so AttributionManager has
      // had a chance to persist them and the current query string is fresh.
      const target = appendUtmToUrl(base);
      // Hold ~1s so Wistia's "registered" confirmation reads before leaving, then
      // `replace` (not `assign`) so hitting Back from the thank-you page does not
      // land the visitor back on the already-submitted form.
      window.setTimeout(() => {
        window.location.replace(target);
      }, 1000);
    };

    // Approach 1: catch a Wistia submit event (names vary across embed versions).
    WISTIA_SUBMIT_EVENTS.forEach(name => {
      document.addEventListener(name, redirect);
    });

    // Approach 2 (fallback): Wistia's live-event form does not reliably emit a
    // catchable event, so we also watch its shadow DOM flip from "has input
    // fields" (form on screen) to "no fields" (its success/"registered" panel)
    // and redirect then. Two guards keep that heuristic honest:
    // - `sawFields`: a visitor whose success panel shows with fields never
    //   present is not redirected.
    // - `interacted`: Wistia also removes the fields WITHOUT a submission — it
    //   briefly paints the form and then swaps to "You're registered" for a
    //   cookied returning registrant, and swaps to the countdown/live panel
    //   when the event starts or ends. Those swaps happen with zero user input,
    //   and treating them as submissions redirected every such visitor straight
    //   to the thank-you page (and fired its conversion tags). A real
    //   submission always involves touching the form first, so the fallback
    //   only arms once a focus/pointer/key event has crossed the embed.
    //   Wistia's shadow root is open and these are composed events, so they
    //   surface on the host element via composedPath().
    let sawFields = false;
    let interacted = false;
    let tries = 0;
    let observer: MutationObserver | undefined;

    const markInteracted = (event: Event) => {
      const path = event.composedPath?.() ?? [];
      const withinEmbed = path.some(node => node instanceof Element && node.tagName === 'WISTIA-FORM');
      if (withinEmbed || (event.target instanceof Element && event.target.closest('wistia-form'))) {
        interacted = true;
      }
    };
    const interactionEvents: (keyof DocumentEventMap)[] = ['focusin', 'keydown', 'pointerdown'];
    interactionEvents.forEach(name => {
      document.addEventListener(name, markInteracted, { capture: true, passive: true });
    });

    const getRoot = (): ShadowRoot | null => {
      const el = document.querySelector(`wistia-form[form-id="${formId}"], wistia-form[live-event-id="${formId}"]`);

      return el?.shadowRoot ?? null;
    };
    const check = () => {
      const root = getRoot();
      if (!root) {
        return;
      }
      if (root.querySelector('input, textarea, select')) {
        sawFields = true;
      } else if (sawFields && interacted) {
        redirect();
      }
    };

    const poll = window.setInterval(() => {
      tries += 1;
      const root = getRoot();
      if (!root) {
        if (tries > 120) {
          window.clearInterval(poll);
        }

        return;
      }
      window.clearInterval(poll);
      check();
      observer = new MutationObserver(check);
      observer.observe(root, { childList: true, subtree: true });
    }, 300);

    return () => {
      WISTIA_SUBMIT_EVENTS.forEach(name => {
        document.removeEventListener(name, redirect);
      });
      interactionEvents.forEach(name => {
        document.removeEventListener(name, markInteracted, { capture: true } as EventListenerOptions);
      });
      window.clearInterval(poll);
      observer?.disconnect();
    };
  }, [redirectUrl, formId]);

  if (!formId) {
    return (
      <div className="flex items-center justify-center rounded-lg bg-gray-800/40 px-6 py-10 text-center text-gray-400">
        Select a Wistia form in the Builder editor to display it.
      </div>
    );
  }

  // Wistia lays fields out inside its shadow DOM; a `data-layout` attribute on
  // the host lets the `wistia-form::part(field)` rules in global.css force each
  // field full-width. A data attribute (not className) is used so it renders
  // reliably on the custom element regardless of React's class handling.
  return (
    <>
      {embedType === 'form' ? (
        <wistia-form form-id={formId} style={lightTheme} data-layout={layout} />
      ) : (
        <wistia-form live-event-id={formId} style={lightTheme} data-layout={layout} />
      )}
      <Script id="wistia-form-js" src="https://fast.wistia.net/assets/external/form.js" strategy="afterInteractive" />
    </>
  );
};

export default WistiaFormEmbed;
